import React, { useEffect, useState, useCallback } from 'react';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import Badge from '@cloudscape-design/components/badge';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import SpaceBetween from '@cloudscape-design/components/space-between';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Spinner from '@cloudscape-design/components/spinner';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import Pagination from '@cloudscape-design/components/pagination';
import Alert from '@cloudscape-design/components/alert';
import { fetchSubmissions, excludeRecord, regenerateOutput, SubmissionRecord, SubmissionStatus } from '../services/api';

/* ── Public types (exported for property testing) ── */

export interface MergedRecord {
  index: number;
  accountType: string;
  record: object;
  status: SubmissionStatus | 'not-submitted';
  swiftIdApplicationId: string | null;
  swiftIdResponse: Record<string, unknown> | null;
  error: Record<string, unknown> | null;
}

/* ── Pure merge function (exported for property testing) ── */

export function mergeRecordsWithSubmissions(
  records: object[],
  submissions: SubmissionRecord[],
  rowGroupings?: Record<string, number[]>,
): MergedRecord[] {
  // Invert rowGroupings: index → accountType
  const indexToType: Record<number, string> = {};
  if (rowGroupings) {
    for (const [accountType, indices] of Object.entries(rowGroupings)) {
      for (const idx of indices) {
        indexToType[idx] = accountType;
      }
    }
  }

  const submissionByIndex = new Map<number, SubmissionRecord>();
  for (const sub of submissions) {
    submissionByIndex.set(sub.recordIndex, sub);
  }

  return records.map((record, i) => {
    const sub = submissionByIndex.get(i);
    return {
      index: i,
      accountType: sub?.accountType ?? indexToType[i] ?? 'unknown',
      record,
      status: sub ? sub.status : ('not-submitted' as const),
      swiftIdApplicationId: sub?.swiftIdApplicationId ?? null,
      swiftIdResponse: sub?.swiftIdResponse ?? null,
      error: sub?.error ?? null,
    };
  });
}

/**
 * Extracts individual records from the agent output preview.
 *
 * The outputFilePreview from the API can be:
 * - A single-element array containing `{ records: [...], rowGroupings: {...} }`
 * - A flat array of record objects (already unwrapped)
 *
 * This function normalises both shapes into a flat array of individual records
 * and extracts embedded rowGroupings if present.
 */
export function extractRecords(
  outputFilePreview: object[],
  externalRowGroupings?: Record<string, number[]>,
): { records: object[]; rowGroupings?: Record<string, number[]> } {
  if (outputFilePreview.length === 0) {
    return { records: [], rowGroupings: externalRowGroupings };
  }

  // Check if the first element has a `records` array (wrapped format)
  const first = outputFilePreview[0] as Record<string, unknown>;
  if (first && Array.isArray(first.records)) {
    const embedded = first.rowGroupings as Record<string, number[]> | undefined;
    return {
      records: first.records as object[],
      rowGroupings: externalRowGroupings ?? embedded,
    };
  }

  // Already a flat array of records
  return { records: outputFilePreview, rowGroupings: externalRowGroupings };
}

/* ── Component ── */

interface OutputPreviewTabProps {
  outputFilePreview: object[];
  jobId: string;
  rowGroupings?: Record<string, number[]>;
  hasChangedMappings?: boolean;
  onOutputRefreshed?: (newPreview: object[]) => void;
}

function StatusDisplay({ merged }: { merged: MergedRecord }) {
  if (merged.status === 'excluded') {
    return <StatusIndicator type="warning">Excluded from submission</StatusIndicator>;
  }
  if (merged.status === 'submitted') {
    return (
      <StatusIndicator type="success">
        Submitted (id: {merged.swiftIdApplicationId})
      </StatusIndicator>
    );
  }
  if (merged.status === 'failed') {
    const errorMsg = merged.error
      ? (merged.error as Record<string, unknown>).message as string
        ?? (Array.isArray((merged.error as Record<string, unknown>).validationErrors)
          ? ((merged.error as Record<string, unknown>).validationErrors as string[]).join('; ')
          : 'Unknown error')
      : 'Unknown error';
    return <StatusIndicator type="error">Failed — {errorMsg}</StatusIndicator>;
  }
  if (merged.status === 'pending') {
    return <StatusIndicator type="in-progress">Pending</StatusIndicator>;
  }
  return <StatusIndicator type="stopped">Not Submitted</StatusIndicator>;
}

export default function OutputPreviewTab({ outputFilePreview, jobId, rowGroupings, hasChangedMappings, onOutputRefreshed }: OutputPreviewTabProps) {
  const [mergedRecords, setMergedRecords] = useState<MergedRecord[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [excluding, setExcluding] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [currentPreview, setCurrentPreview] = useState(outputFilePreview);

  // Extract individual records from the (possibly wrapped) output preview
  const { records: individualRecords, rowGroupings: effectiveRowGroupings } = React.useMemo(
    () => extractRecords(currentPreview, rowGroupings),
    [currentPreview, rowGroupings],
  );

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const submissions = await fetchSubmissions(jobId);
      setMergedRecords(mergeRecordsWithSubmissions(individualRecords, submissions, effectiveRowGroupings));
    } catch {
      setMergedRecords(mergeRecordsWithSubmissions(individualRecords, [], effectiveRowGroupings));
    } finally {
      setLoading(false);
    }
  }, [jobId, individualRecords, effectiveRowGroupings]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const handleExcludeToggle = useCallback(async () => {
    const selected = mergedRecords[selectedIndex];
    if (!selected) return;

    const shouldExclude = selected.status !== 'excluded';
    setExcluding(true);
    try {
      await excludeRecord(jobId, selected.index, shouldExclude);
      await loadSubmissions();
    } catch (err) {
      console.error('Failed to toggle exclude', err);
    } finally {
      setExcluding(false);
    }
  }, [jobId, mergedRecords, selectedIndex, loadSubmissions]);

  const handleRefreshOutput = useCallback(async () => {
    setRefreshing(true);
    try {
      const result = await regenerateOutput(jobId);
      // Wrap in array to match the outputFilePreview format
      const newPreview = [result];
      setCurrentPreview(newPreview);
      onOutputRefreshed?.(newPreview);
    } catch (err) {
      console.error('Failed to regenerate output', err);
    } finally {
      setRefreshing(false);
    }
  }, [jobId, onOutputRefreshed]);

  if (individualRecords.length === 0) {
    return <Box>No output file preview available</Box>;
  }

  if (loading) {
    return (
      <Container>
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Spinner size="normal" />
          <Box>Loading submission statuses…</Box>
        </SpaceBetween>
      </Container>
    );
  }

  const selected = mergedRecords[selectedIndex];
  if (!selected) {
    return <Box>No records available</Box>;
  }

  const options: SelectProps.Option[] = mergedRecords.map((r) => ({
    label: `Record ${r.index + 1} — ${r.accountType}${r.status === 'excluded' ? ' (excluded)' : ''}`,
    value: String(r.index),
  }));

  const recordJson = JSON.stringify(selected.record, null, 2);
  const isExcluded = selected.status === 'excluded';
  const isSubmitted = selected.status === 'submitted';

  const totalRecords = mergedRecords.length;

  return (
    <SpaceBetween size="m">
      {/* Stale output warning when mappings have been changed */}
      {hasChangedMappings && (
        <Alert
          type="warning"
          action={
            <Button onClick={handleRefreshOutput} loading={refreshing}>
              Refresh Output
            </Button>
          }
        >
          Mappings have been modified — output may be stale.
        </Alert>
      )}

      <Container>
        <SpaceBetween size="m">
          {/* Record selector (dropdown left) + pagination (right) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <Select
                selectedOption={options[selectedIndex] ?? null}
                onChange={({ detail }) => setSelectedIndex(Number(detail.selectedOption.value))}
                options={options}
                placeholder="Select a record"
              />
            </div>
            <Pagination
              currentPageIndex={selectedIndex + 1}
              pagesCount={totalRecords}
              onChange={({ detail }) => setSelectedIndex(detail.currentPageIndex - 1)}
              ariaLabels={{
                nextPageLabel: 'Next record',
                previousPageLabel: 'Previous record',
                pageLabel: (pageNumber) => `Record ${pageNumber}`,
              }}
            />
          </div>

          {/* Endpoint badge + status + actions row */}
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Badge color="blue">POST</Badge>
            <Box variant="code">/applications/{selected.accountType}/prefill/v2</Box>
          </SpaceBetween>

          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <StatusDisplay merged={selected} />
            {/* Exclude/Include toggle — not available for already-submitted records */}
            {!isSubmitted && (
              <Button
                variant={isExcluded ? 'normal' : 'link'}
                loading={excluding}
                onClick={handleExcludeToggle}
              >
                {isExcluded ? 'Include in submission' : 'Exclude from submission'}
              </Button>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      {/* Record JSON with copy button */}
      <Container
        header={
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Box variant="h3">Record Payload</Box>
            <CopyToClipboard
              copyButtonAriaLabel="Copy record payload"
              copyErrorText="Failed to copy"
              copySuccessText="Copied to clipboard"
              textToCopy={recordJson}
            />
          </SpaceBetween>
        }
      >
        <Box variant="code">
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {recordJson}
          </pre>
        </Box>
      </Container>

      {/* SwiftID response (only for submitted records) */}
      {selected.status === 'submitted' && selected.swiftIdResponse && (
        <ExpandableSection headerText="SwiftID API Response">
          <Box variant="code">
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(selected.swiftIdResponse, null, 2)}
            </pre>
          </Box>
        </ExpandableSection>
      )}

      {/* Error details (for failed records) */}
      {selected.status === 'failed' && selected.error && (
        <ExpandableSection headerText="Error Details">
          <Box variant="code">
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(selected.error, null, 2)}
            </pre>
          </Box>
        </ExpandableSection>
      )}
    </SpaceBetween>
  );
}
